import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Share2, 
  Twitter, 
  Facebook, 
  MessageCircle,
  Link2,
  Check,
  X,
  Download,
  Flame
} from "lucide-react";

// Generate shareable text for achievements
export function getShareText(type, data) {
  const appUrl = "https://www.truenorth-zone.com/";
  
  switch (type) {
    case "badge":
      return {
        title: `I earned the "${data.name}" badge on Compass!`,
        text: `${data.description} 🏆 Join me on this journey of transformation.`,
        hashtags: "Compass,PersonalGrowth,TrueNorth"
      };
    case "streak":
      return {
        title: `${data.days} Day Streak on Compass!`,
        text: `I just hit a ${data.days} day streak on Compass. Showing up daily. 🔥 ${appUrl}`,
        hashtags: "Compass,DailyStreak,PersonalGrowth"
      };
    case "streak_badge":
      return {
        title: `${data.days} Day Streak!`,
        text: `I just hit a ${data.days} day streak on Compass. Showing up daily. 🔥 ${appUrl}`,
        hashtags: "Compass,DailyStreak"
      };
    case "mini_quest_first":
      return {
        title: "First Mini-Quest Complete!",
        text: `Just completed my first 7-day challenge on Compass. One week of real work done. 💛 ${appUrl}`,
        hashtags: "Compass,MiniQuest,PersonalGrowth"
      };
    case "milestone":
      return {
        title: `Milestone Reached on Compass!`,
        text: `${data.message} 🎯 Taking daily steps toward my true north.`,
        hashtags: "Compass,Milestone,Transformation"
      };
    case "reflection":
      return {
        title: "Reflection from my Compass journey",
        text: data.text?.substring(0, 200) + (data.text?.length > 200 ? "..." : ""),
        hashtags: "Compass,Reflection,PersonalGrowth"
      };
    default:
      return {
        title: "Join me on Compass",
        text: "I'm on a journey of personal transformation with Compass.",
        hashtags: "Compass,PersonalGrowth"
      };
  }
}

// Share to different platforms
export function shareToTwitter(title, text, hashtags) {
  const tweetText = encodeURIComponent(`${title}\n\n${text}`);
  const hashtagString = encodeURIComponent(hashtags);
  window.open(
    `https://twitter.com/intent/tweet?text=${tweetText}&hashtags=${hashtagString}`,
    "_blank",
    "noopener,noreferrer,width=600,height=400"
  );
}

export function shareToFacebook(title, text) {
  const shareText = encodeURIComponent(`${title}\n\n${text}`);
  window.open(
    `https://www.facebook.com/sharer/sharer.php?quote=${shareText}`,
    "_blank",
    "noopener,noreferrer,width=600,height=400"
  );
}

export function shareToWhatsApp(title, text) {
  const message = encodeURIComponent(`${title}\n\n${text}\n\nhttps://truenorth-zone.com`);
  window.open(
    `https://wa.me/?text=${message}`,
    "_blank",
    "noopener,noreferrer"
  );
}

export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy:", err);
    return false;
  }
}

// Share button component
export function ShareButton({ type, data, variant = "ghost", size = "sm", className = "" }) {
  const [showDialog, setShowDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const shareData = getShareText(type, data);
  const fullText = `${shareData.title}\n\n${shareData.text}\n\nhttps://truenorth-zone.com`;

  const handleCopy = async () => {
    const success = await copyToClipboard(fullText);
    if (success) {
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Failed to copy");
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareData.title,
          text: shareData.text,
          url: "https://truenorth-zone.com"
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          setShowDialog(true);
        }
      }
    } else {
      setShowDialog(true);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleNativeShare}
        data-testid="share-button"
      >
        <Share2 className="w-4 h-4 mr-2" />
        Share
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md bg-[#f5f9f5]">
          <DialogHeader>
            <DialogTitle className="text-[#a69373]">Share Your Achievement</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Preview card - Enhanced for streaks */}
            {type === "streak" ? (
              <div className="bg-gradient-to-br from-[#2d4a44] to-[#163D33] rounded-2xl p-6 text-center relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-2 left-2 w-20 h-20 rounded-full bg-orange-400 blur-2xl" />
                  <div className="absolute bottom-2 right-2 w-16 h-16 rounded-full bg-yellow-400 blur-2xl" />
                </div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <Flame className="w-8 h-8 text-white" />
                  </div>
                  
                  <p className="text-[#a69373] text-sm font-medium mb-1">🔥 STREAK MILESTONE</p>
                  <p className="text-white text-4xl font-heading mb-1">{data.days}</p>
                  <p className="text-white/80 text-lg">Day Streak!</p>
                  
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-[#a69373] text-xs">
                      Join my transformation journey on Compass
                    </p>
                    <p className="text-white/60 text-xs mt-1">truenorth-zone.com</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-[#d4e4d4] to-[#c5e0c5] rounded-xl p-4">
                <p className="font-heading text-[#a69373] text-lg mb-2">{shareData.title}</p>
                <p className="text-[#a69373]/70 text-sm">{shareData.text}</p>
              </div>
            )}

            {/* Share options */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="flex items-center gap-2 h-12 bg-white"
                onClick={() => {
                  shareToTwitter(shareData.title, shareData.text, shareData.hashtags);
                  setShowDialog(false);
                }}
              >
                <Twitter className="w-5 h-5 text-[#1DA1F2]" />
                <span className="text-[#a69373]">Twitter/X</span>
              </Button>
              
              <Button
                variant="outline"
                className="flex items-center gap-2 h-12 bg-white"
                onClick={() => {
                  shareToFacebook(shareData.title, shareData.text);
                  setShowDialog(false);
                }}
              >
                <Facebook className="w-5 h-5 text-[#1877F2]" />
                <span className="text-[#a69373]">Facebook</span>
              </Button>
              
              <Button
                variant="outline"
                className="flex items-center gap-2 h-12 bg-white"
                onClick={() => {
                  shareToWhatsApp(shareData.title, shareData.text);
                  setShowDialog(false);
                }}
              >
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                <span className="text-[#a69373]">WhatsApp</span>
              </Button>
              
              <Button
                variant="outline"
                className="flex items-center gap-2 h-12 bg-white"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Link2 className="w-5 h-5 text-[#a69373]/60" />
                )}
                <span className="text-[#a69373]">{copied ? "Copied!" : "Copy Link"}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Shareable achievement card component
export function ShareableCard({ type, data, onClose }) {
  const shareData = getShareText(type, data);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden animate-celebrate">
        {/* Card header with gradient */}
        <div className="bg-gradient-to-br from-[#7a9e7a] to-[#5a8a5a] p-6 text-center">
          <h3 className="text-white text-xl font-heading mb-2">{shareData.title}</h3>
          <p className="text-white/80 text-sm">{shareData.text}</p>
        </div>
        
        {/* Share options */}
        <div className="p-4 space-y-3">
          <p className="text-[#a69373]/60 text-sm text-center mb-4">Share your achievement</p>
          
          <div className="flex justify-center gap-4">
            <button
              onClick={() => shareToTwitter(shareData.title, shareData.text, shareData.hashtags)}
              className="w-12 h-12 rounded-full bg-[#1DA1F2]/10 flex items-center justify-center hover:bg-[#1DA1F2]/20 transition-colors"
            >
              <Twitter className="w-6 h-6 text-[#1DA1F2]" />
            </button>
            
            <button
              onClick={() => shareToFacebook(shareData.title, shareData.text)}
              className="w-12 h-12 rounded-full bg-[#1877F2]/10 flex items-center justify-center hover:bg-[#1877F2]/20 transition-colors"
            >
              <Facebook className="w-6 h-6 text-[#1877F2]" />
            </button>
            
            <button
              onClick={() => shareToWhatsApp(shareData.title, shareData.text)}
              className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center hover:bg-[#25D366]/20 transition-colors"
            >
              <MessageCircle className="w-6 h-6 text-[#25D366]" />
            </button>
          </div>
          
          <Button
            variant="ghost"
            className="w-full text-[#a69373]/50 hover:text-[#a69373]"
            onClick={onClose}
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ShareButton;
